import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  TextField,
  Tabs,
  Tab,
  Box,
  Popover,
  SvgIcon,
  InputAdornment,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFontAwesomeIcons, filterIcons } from "./icon-selector-utils";
import IconItem from "./icon-selector-item";

// The FontAwesome regular set is ~4.8k icons; rendering them all at once stalls
// the popover on open. Render a page at a time and grow it as the user scrolls.
const ICON_PAGE_SIZE = 60;
// Grow the list this far before the true bottom for a seamless scroll.
const SCROLL_THRESHOLD_PX = 240;

export const IconSearchSelect = ({
  customIcons = [],
  value = null,
  onChange,
  label,
  variant = "outlined",
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [filteredIcons, setFilteredIcons] = useState([]);
  const [fontAwesomeIcons, setFontAwesomeIcons] = useState([]);
  const [selectedIcon, setSelectedIcon] = useState({
    name: value,
    icon: value,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleCount, setVisibleCount] = useState(ICON_PAGE_SIZE);
  const scrollRef = useRef(null);

  useEffect(() => {
    setFontAwesomeIcons(getFontAwesomeIcons());
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const faResults = filterIcons(fontAwesomeIcons, searchTerm);
      const customResults = filterIcons(
        Object.keys(customIcons).map((icon) => {
          return { name: icon, customIcon: true };
        }),
        searchTerm
      );
      setFilteredIcons([...faResults, ...customResults]);
    } else {
      setFilteredIcons([]);
    }
  }, [searchTerm]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleIconSelect = (icon) => {
    setSelectedIcon(icon);
    onChange(icon.icon);
    setAnchorEl(null);
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // The list backing the active view: search results, FontAwesome icons, or
  // custom icons. Each entry is normalised to `{ name, customIcon? }`.
  const currentList = searchTerm
    ? filteredIcons
    : activeTab === 0
      ? fontAwesomeIcons
      : Object.keys(customIcons).map((name) => ({ name, customIcon: true }));

  // Restart paging (and scroll to top) whenever the view changes or the popover
  // reopens, so every view begins at the first page.
  useEffect(() => {
    setVisibleCount(ICON_PAGE_SIZE);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [searchTerm, activeTab, anchorEl]);

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD_PX) {
      setVisibleCount((count) =>
        count < currentList.length ? count + ICON_PAGE_SIZE : count,
      );
    }
  };

  // Icons are fixed-size tiles (see IconItem), so they simply flow and wrap in
  // a flex row — no Grid column sizing needed.
  const renderEntry = (entry) => {
    if (searchTerm) {
      return (
        <IconItem
          key={entry.name}
          icon={entry.customIcon ? customIcons[entry.name] : entry}
          onSelect={handleIconSelect}
          isCustomIcon={entry.customIcon}
          iconName={entry.name}
        />
      );
    }
    if (activeTab === 0) {
      return <IconItem key={entry.name} icon={entry} onSelect={handleIconSelect} />;
    }
    return (
      <IconItem
        key={entry.name}
        icon={customIcons[entry.name]}
        isCustomIcon={true}
        iconName={entry.name}
        onSelect={handleIconSelect}
      />
    );
  };

  return (
    <Box>
      <TextField
        fullWidth
        variant={variant}
        label={label}
        placeholder={t("forms.selectIcon")}
        value={
          typeof selectedIcon.icon === "string"
            ? selectedIcon.icon
            : selectedIcon?.icon?.iconName ??
              (typeof selectedIcon?.name === "string" ? selectedIcon.name : "")
        }
        disabled={disabled}
        onClick={disabled ? undefined : handlePopoverOpen}
        slotProps={{
          input: {
            readOnly: true,
            startAdornment: selectedIcon ? (
              <InputAdornment position="start">
                {typeof selectedIcon.icon === "string" ? (
                  <FontAwesomeIcon icon={`fa-regular ${selectedIcon.icon}`} />
                ) : Array.isArray(selectedIcon?.icon?.icon) ? (
                  <FontAwesomeIcon icon={selectedIcon.icon} />
                ) : (
                  <Box component="span" sx={{ fontSize: "24px" }}>
                    <SvgIcon>
                      <svg
                        viewBox={`0 0 ${selectedIcon?.icon?.width} ${selectedIcon?.icon?.height}`}
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path d={selectedIcon?.icon?.pathData} />
                      </svg>
                    </SvgIcon>
                  </Box>
                )}
              </InputAdornment>
            ) : null,
          },
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{ paper: { sx: { maxHeight: "80vh" } } }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 800,
            height: 400,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: "background.paper",
              p: 2,
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t("forms.searchIcons")}
              value={searchTerm}
              onChange={handleSearch}
              slotProps={{
                input: {
                  startAdornment: (
                    <FontAwesomeIcon icon="search" style={{ marginRight: 8 }} />
                  ),
                },
              }}
            />
            {!searchTerm && (
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ mt: 2 }}>
                <Tab label={t("forms.fontAwesomeIcons")} />
                <Tab label={t("forms.kmarIcons")} />
              </Tabs>
            )}
          </Box>
          <Box
            ref={scrollRef}
            onScroll={handleScroll}
            data-testid="icon-scroll-container"
            sx={{ flexGrow: 1, overflowY: "auto", p: 2 }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "center",
              }}
            >
              {currentList.slice(0, visibleCount).map(renderEntry)}
            </Box>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

IconSearchSelect.propTypes = {
  customIcons: PropTypes.array,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
};

export default IconSearchSelect;
